import React, { useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/src/style.css";
import "../styles/DateRange.css";

function getDigitIndexFromCaret(maskedValue, caretPosition) {
  let digitIndex = 0;

  for (let i = 0; i < caretPosition; i += 1) {
    if (/\d/.test(maskedValue[i])) {
      digitIndex += 1;
    }
  }

  return digitIndex;
}

function setCaretPosition(input, pos) {
  requestAnimationFrame(() => {
    input.setSelectionRange(pos, pos);
  });
}

function parseDDMMYYYY(value) {
  if (!value || typeof value !== "string") return undefined;
  const parts = value.split("/");
  if (parts.length !== 3) return undefined;
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year) return undefined;
  const result = new Date(year, month - 1, day);
  if (Number.isNaN(result?.getTime())) return undefined;
  return result;
}

function toDDMMYYYY(date) {
  if (!date || Number.isNaN(date?.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function toMMDDYYYY(date) {
  if (!date || Number.isNaN(date?.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function formatMaskedDateFromDigits(rawDigits) {
  const digits = (rawDigits || "").replace(/\D/g, "").slice(0, 8);

  let month = "MM";
  let day = "DD";
  let year = "YYYY";

  let rest = digits;

  if (rest.length >= 1) {
    const first = rest[0];

    if (/[2-9]/.test(first)) {
      month = `0${first}`;
      rest = rest.slice(1);
    } else if (rest.length >= 2) {
      const candidate = rest.slice(0, 2);
      const monthNum = Number(candidate);

      if (monthNum >= 1 && monthNum <= 12) {
        month = candidate;
        rest = rest.slice(2);
      } else {
        month = `0${first}`;
        rest = rest.slice(1);
      }
    } else if (/[0-1]/.test(first)) {
      month = first === "0" ? "0" : first;
      rest = rest.slice(1);
    }
  }

  if (rest.length >= 1) {
    const first = rest[0];

    if (/[4-9]/.test(first)) {
      day = `0${first}`;
      rest = rest.slice(1);
    } else if (rest.length >= 2) {
      const candidate = rest.slice(0, 2);
      const dayNum = Number(candidate);

      if (dayNum >= 1 && dayNum <= 31) {
        day = candidate;
        rest = rest.slice(2);
      } else {
        day = `0${first}`;
        rest = rest.slice(1);
      }
    } else if (/[0-3]/.test(first)) {
      day = first;
      rest = "";
    }
  }

  if (rest.length > 0) {
    year = `${rest}${"YYYY".slice(rest.length)}`;
  }

  return `${month}/${day}/${year}`;
}

function dateStringToDigits(value) {
  if (!value || typeof value !== "string") return "";
  return value.replace(/\D/g, "").slice(0, 8);
}

function isValidDateParts(day, month, year) {
  if (!day || !month || !year) return false;
  const candidate = new Date(year, month - 1, day);
  return (
    candidate.getFullYear() === year &&
    candidate.getMonth() === month - 1 &&
    candidate.getDate() === day
  );
}

function parseTypedDate(value) {
  if (!value || typeof value !== "string") return undefined;

  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return undefined;

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);

  if (!isValidDateParts(day, month, year)) return undefined;
  return new Date(year, month - 1, day);
}

export default function DateRangePicker({
  fromValue,
  toValue,
  onRangeChange,
  compact = false,
  buttonLike = false,
  buttonLabel = "Date Range",
  fromLabel = "From",
  toLabel = "To",
  className = "",
  inputsClassName = "",
  inputColClassName = "",
  fieldClassName = "",
  popoverClassName = "",
  calendarCardClassName = "",
  style = {},
  inputsStyle = {},
  fromColStyle = {},
  toColStyle = {},
  fromFieldStyle = {},
  toFieldStyle = {},
  popoverStyle = {},
  calendarCardStyle = {},
  closeSignal,
}) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const wrapRef = useRef(null);

  const selectedRange = useMemo(
    () => ({
      from: parseDDMMYYYY(fromValue),
      to: parseDDMMYYYY(toValue),
    }),
    [fromValue, toValue],
  );

  useEffect(() => {
    if (!open) return;

    const onOutsideClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onOutsideClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutsideClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [closeSignal]);

  useEffect(() => {
    setDraftFrom(
      selectedRange.from
        ? dateStringToDigits(toMMDDYYYY(selectedRange.from))
        : "",
    );
  }, [fromValue]);

  useEffect(() => {
    setDraftTo(
      selectedRange.to ? dateStringToDigits(toMMDDYYYY(selectedRange.to)) : "",
    );
  }, [toValue]);

  const applyRange = (range) => {
    const nextFrom = range?.from ? toDDMMYYYY(range.from) : "";
    const nextTo = range?.to ? toDDMMYYYY(range.to) : "";
    setDraftFrom(range?.from ? dateStringToDigits(toMMDDYYYY(range.from)) : "");
    setDraftTo(range?.to ? dateStringToDigits(toMMDDYYYY(range.to)) : "");
    onRangeChange?.({
      from: nextFrom,
      to: nextTo,
    });
  };

  const clearRange = () => {
    setDraftFrom("");
    setDraftTo("");
    onRangeChange?.({ from: "", to: "" });
  };

  const buttonValue =
    fromValue && toValue
      ? `${fromValue} - ${toValue}`
      : fromValue || toValue || buttonLabel;

  const commitFromValue = () => {
    if (!draftFrom) {
      onRangeChange?.({ from: "", to: toValue || "" });
      return;
    }

    if (draftFrom.length !== 8) {
      setDraftFrom(
        selectedRange.from
          ? dateStringToDigits(toMMDDYYYY(selectedRange.from))
          : "",
      );
      return;
    }

    const parsed = parseTypedDate(formatMaskedDateFromDigits(draftFrom));
    if (!parsed) {
      setDraftFrom(
        selectedRange.from
          ? dateStringToDigits(toMMDDYYYY(selectedRange.from))
          : "",
      );
      return;
    }

    const normalized = toDDMMYYYY(parsed);
    setDraftFrom(dateStringToDigits(toMMDDYYYY(parsed)));
    onRangeChange?.({ from: normalized, to: toValue || "" });
  };

  const commitToValue = () => {
    if (!draftTo) {
      onRangeChange?.({ from: fromValue || "", to: "" });
      return;
    }

    if (draftTo.length !== 8) {
      setDraftTo(
        selectedRange.to
          ? dateStringToDigits(toMMDDYYYY(selectedRange.to))
          : "",
      );
      return;
    }

    const parsed = parseTypedDate(formatMaskedDateFromDigits(draftTo));
    if (!parsed) {
      setDraftTo(
        selectedRange.to
          ? dateStringToDigits(toMMDDYYYY(selectedRange.to))
          : "",
      );
      return;
    }

    const normalized = toDDMMYYYY(parsed);
    setDraftTo(dateStringToDigits(toMMDDYYYY(parsed)));
    onRangeChange?.({ from: fromValue || "", to: normalized });
  };

  return (
    <div
      className={`drp ${compact ? "drp-compact" : ""} ${buttonLike ? "drp-buttonlike" : ""} ${className}`.trim()}
      ref={wrapRef}
      style={style}
    >
      {buttonLike ? (
        <button
          type="button"
          className="drp-trigger-button"
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="drp-cal-icon" aria-hidden="true" />
          <span className="drp-trigger-text">{buttonValue}</span>
        </button>
      ) : (
        <>
      {!compact && (
        <div className="drp-top">
          <div className="drp-title">Date Range</div>
          <button type="button" className="drp-clear" onClick={clearRange}>
            Clear
          </button>
        </div>
      )}

      <div
        className={`drp-inputs ${inputsClassName}`.trim()}
        style={inputsStyle}
      >
        <div
          className={`drp-input-col ${inputColClassName}`.trim()}
          style={fromColStyle}
        >
          <div className="drp-label">{fromLabel}</div>
          <div
            className={`drp-field ${fieldClassName}`.trim()}
            style={fromFieldStyle}
          >
            <input
              className="drp-input-value"
              inputMode="numeric"
              value={draftFrom ? formatMaskedDateFromDigits(draftFrom) : ""}
              placeholder="MM/DD/YYYY"
              onChange={(e) =>
                setDraftFrom(e.target.value.replace(/\D/g, "").slice(0, 8))
              }
              onBlur={commitFromValue}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                const input = e.target;
                const maskedValue = formatMaskedDateFromDigits(draftFrom);
                const caretStart = input.selectionStart ?? 0;
                const caretEnd = input.selectionEnd ?? 0;

                if (e.key === "Enter") {
                  e.preventDefault();
                  commitFromValue();
                  return;
                }

                if (e.key === "Backspace") {
                  e.preventDefault();
                  if (!draftFrom.length) return;

                  const startDigitIndex = getDigitIndexFromCaret(
                    maskedValue,
                    caretStart,
                  );
                  const endDigitIndex = getDigitIndexFromCaret(
                    maskedValue,
                    caretEnd,
                  );

                  let nextDigits = draftFrom;
                  let nextCaret = caretStart;

                  if (caretStart !== caretEnd) {
                    nextDigits =
                      draftFrom.slice(0, startDigitIndex) +
                      draftFrom.slice(endDigitIndex);
                  } else if (startDigitIndex > 0) {
                    nextDigits =
                      draftFrom.slice(0, startDigitIndex - 1) +
                      draftFrom.slice(startDigitIndex);
                    nextCaret = caretStart - 1;
                  }

                  setDraftFrom(nextDigits);
                  if (!nextDigits) {
                    onRangeChange?.({ from: "", to: toValue || "" });
                  }
                  setCaretPosition(input, Math.max(0, nextCaret));
                  return;
                }

                if (e.key === "Delete") {
                  e.preventDefault();
                  if (!draftFrom.length) return;

                  if (caretStart === caretEnd && draftFrom.length === 8) {
                    setDraftFrom("");
                    onRangeChange?.({ from: "", to: toValue || "" });
                    return;
                  }

                  const startDigitIndex = getDigitIndexFromCaret(
                    maskedValue,
                    caretStart,
                  );
                  const endDigitIndex = getDigitIndexFromCaret(
                    maskedValue,
                    caretEnd,
                  );

                  const nextDigits =
                    caretStart !== caretEnd
                      ? draftFrom.slice(0, startDigitIndex) +
                        draftFrom.slice(endDigitIndex)
                      : draftFrom.slice(0, startDigitIndex) +
                        draftFrom.slice(startDigitIndex + 1);

                  setDraftFrom(nextDigits);
                  if (!nextDigits) {
                    onRangeChange?.({ from: "", to: toValue || "" });
                  }
                  setCaretPosition(input, caretStart);
                }
              }}
            />
            <button
              type="button"
              className="drp-icon-button"
              onClick={() => setOpen(true)}
            >
              <span className="drp-cal-icon" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="drp-separator">-</div>
        <div
          className={`drp-input-col ${inputColClassName}`.trim()}
          style={toColStyle}
        >
          <div className="drp-label">{toLabel}</div>
          <div
            className={`drp-field ${fieldClassName}`.trim()}
            style={toFieldStyle}
          >
            <input
              className="drp-input-value"
              inputMode="numeric"
              value={draftTo ? formatMaskedDateFromDigits(draftTo) : ""}
              placeholder="MM/DD/YYYY"
              onChange={(e) =>
                setDraftTo(e.target.value.replace(/\D/g, "").slice(0, 8))
              }
              onBlur={commitToValue}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                const input = e.target;
                const maskedValue = formatMaskedDateFromDigits(draftTo);
                const caretStart = input.selectionStart ?? 0;
                const caretEnd = input.selectionEnd ?? 0;

                if (e.key === "Enter") {
                  e.preventDefault();
                  commitToValue();
                  return;
                }

                if (e.key === "Backspace") {
                  e.preventDefault();
                  if (!draftTo.length) return;

                  const startDigitIndex = getDigitIndexFromCaret(
                    maskedValue,
                    caretStart,
                  );
                  const endDigitIndex = getDigitIndexFromCaret(
                    maskedValue,
                    caretEnd,
                  );

                  let nextDigits = draftTo;
                  let nextCaret = caretStart;

                  if (caretStart !== caretEnd) {
                    nextDigits =
                      draftTo.slice(0, startDigitIndex) +
                      draftTo.slice(endDigitIndex);
                  } else if (startDigitIndex > 0) {
                    nextDigits =
                      draftTo.slice(0, startDigitIndex - 1) +
                      draftTo.slice(startDigitIndex);
                    nextCaret = caretStart - 1;
                  }

                  setDraftTo(nextDigits);
                  if (!nextDigits) {
                    onRangeChange?.({ from: fromValue || "", to: "" });
                  }
                  setCaretPosition(input, Math.max(0, nextCaret));
                  return;
                }

                if (e.key === "Delete") {
                  e.preventDefault();
                  if (!draftTo.length) return;

                  if (caretStart === caretEnd && draftTo.length === 8) {
                    setDraftTo("");
                    onRangeChange?.({ from: fromValue || "", to: "" });
                    return;
                  }

                  const startDigitIndex = getDigitIndexFromCaret(
                    maskedValue,
                    caretStart,
                  );
                  const endDigitIndex = getDigitIndexFromCaret(
                    maskedValue,
                    caretEnd,
                  );

                  const nextDigits =
                    caretStart !== caretEnd
                      ? draftTo.slice(0, startDigitIndex) +
                        draftTo.slice(endDigitIndex)
                      : draftTo.slice(0, startDigitIndex) +
                        draftTo.slice(startDigitIndex + 1);

                  setDraftTo(nextDigits);
                  if (!nextDigits) {
                    onRangeChange?.({ from: fromValue || "", to: "" });
                  }
                  setCaretPosition(input, caretStart);
                }
              }}
            />
            <button
              type="button"
              className="drp-icon-button"
              onClick={() => setOpen(true)}
            >
              <span className="drp-cal-icon" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
        </>
      )}

      {open && (
        <div
          className={`drp-popover ${popoverClassName}`.trim()}
          style={popoverStyle}
        >
          <div
            className={`drp-calendar-card ${calendarCardClassName}`.trim()}
            style={calendarCardStyle}
          >
            <DayPicker
              mode="range"
              selected={selectedRange}
              onSelect={applyRange}
              numberOfMonths={1}
              showOutsideDays
              fixedWeeks
              captionLayout="label"
              navLayout="around"
            />
            {/* <div className="drp-inputs">
              <div className="drp-input-col">
                <div className="drp-label">From</div>
                <input
                  type="date"
                  className="drp-field"
                  value={toPickerDate(selectedRange.from)}
                  onChange={(e) =>
                    applyRange({
                      from: e.target.value
                        ? new Date(e.target.value)
                        : undefined,
                      to: selectedRange.to,
                    })
                  }
                />
              </div>
              <div className="drp-separator">-</div>
              <div className="drp-input-col">
                <div className="drp-label">To</div>
                <input
                  type="date"
                  className="drp-field"
                  value={toPickerDate(selectedRange.to)}
                  onChange={(e) =>
                    applyRange({
                      from: selectedRange.from,
                      to: e.target.value ? new Date(e.target.value) : undefined,
                    })
                  }
                />
              </div>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
}
